import { createNlsMiddleware, lccstate, quizIsRunning, userstate } from 'lcc-state';
import { logQuiz } from 'logger';
import has from '@dojo/framework/core/has';
import { icache } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { destroy } from '@dojo/framework/core/vdom';
import { Store } from '@dojo/framework/stores/Store';
import css from '../css/quizfolder.m.css';
import { initialState, quizstate, quizstores } from '../support/state';
import { ProgressionWrapper } from './progression-wrapper';
import { QuizTitle } from './quiztitle';
import { WhichPath } from './which-path';
const nls = createNlsMiddleware('quiz');
const ditabookNls = createNlsMiddleware('ditabook');
const factory = create({
    icache,
    quizstate,
    userstate,
    lccstate,
    destroy,
    theme,
    nls,
    ditabookNls
}).properties();
export const QuizFolder = factory(({ middleware: { icache, quizstate, userstate, lccstate, destroy, theme, nls, ditabookNls }, properties }) => {
    const keyprops = () => {
        const { get, path } = userstate;
        const { get: lget, path: lpath } = lccstate;
        return {
            user_id: get(path('user_id')),
            quizPattern: get(path('quizPattern')),
            quizPatterns: lget(lpath('quizpatterns'))
        };
    };
    const { topicid, quizkind: quizkindProp, title: quizTitle } = properties();
    const { quizPattern, quizPatterns } = keyprops();
    const { isPlaceholder } = nls();
    const { quiz: nlsQuiz = '[ditabook] Quiz', flashcards: nlsFlashcards = '[ditabook] flashcards' } = ditabookNls().messages;
    const { get: lget, path: lpath } = lccstate;
    const title = lget(lpath('title'));
    const product = lget(lpath('product'));
    const isFlashcards = /topic\d+-[0-9-]*-flashcards/.test(topicid);
    const pageTitlePrefix = isFlashcards ? nlsFlashcards : nlsQuiz;
    const titleIsAssociationName = title.toLocaleUpperCase() === window.hcstatic.association_name?.toLocaleUpperCase();
    document.title = !titleIsAssociationName
        ? title
        : `${pageTitlePrefix} - ${quizTitle} - ${title.toLocaleUpperCase()} - ${product.toLocaleUpperCase()}`;
    const singleQuizPattern = Object.keys(quizPatterns).length <= 1;
    const hasQuizPattern = singleQuizPattern || (quizPattern !== undefined && quizPattern !== null);
    const isBasicQuiz = quizPattern === '' || singleQuizPattern;
    const useid = isFlashcards || isBasicQuiz ? `${topicid}` : hasQuizPattern ? `${topicid}-${quizPattern}` : `${topicid}`;
    const kind = hasQuizPattern || isFlashcards ? 'quizfolder' : 'quizchoice';
    const allids = icache.getOrSet('allids', new Set());
    const newQuiz = !allids.has(useid);
    allids.add(useid);
    icache.set('allids', allids);
    if (allids.size > 1) {
        logQuiz.info(() => `active quiz ids: ${Array.from(allids).join(', ')}`);
        if (newQuiz) {
            const work = new Set(Array.from(allids));
            work.delete(useid);
            logQuiz.info(() => `force destroying QuizFolder(s) for ${Array.from(work).join(', ')}`);
            for (const id of work) {
                allids.delete(id);
                icache.delete(id);
                icache.delete(`${id}-loggedlocation`);
                const quizstore = quizstores.get(id);
                if (quizstore) {
                    const { get, path } = quizstore;
                    const stopLoading = get(path('stopLoading'));
                    stopLoading && stopLoading();
                    quizIsRunning({ quizRunning: false });
                    quizstores.delete(id);
                }
            }
            icache.set('allids', allids);
        }
    }
    icache.getOrSet(useid, () => {
        destroy(() => {
            logQuiz.info(() => `destroying QuizFolder for ${useid}`);
            const allids = icache.get('allids');
            allids && allids.delete(useid);
            icache.set('allids', allids);
            icache.delete(useid);
            icache.delete(`${useid}-loggedlocation`);
            const quizstore = quizstores.get(useid);
            if (quizstore) {
                const { get, path } = quizstore;
                const stopLoading = get(path('stopLoading'));
                stopLoading && stopLoading();
                quizIsRunning({ quizRunning: false });
                quizstores.delete(useid);
            }
        });
        quizIsRunning({ quizRunning: true });
        return true;
    });
    const { root, microView, microViewWrapper } = theme.classes(css);
    let store = quizstores.get(useid);
    if (!store) {
        store = new Store();
        quizstores.set(useid, store);
        initialState('', store);
    }
    quizstate.setStore(store);
    const { get, path } = quizstate;
    const quiz = get(path('quiz'));
    const quizkind = quiz ? quiz.kind : quizkindProp;
    const key = get(path('instancekey'));
    if (isPlaceholder) {
        return v('div', { key: `${useid}-${key}`, classes: [root] });
    }
    const loggedLocation = icache.getOrSet(`${useid}-loggedlocation`, false);
    if (!loggedLocation && quizkind !== undefined) {
        icache.set(`${useid}-loggedlocation`, true);
        logQuiz.info(() => `quiz-kind ${quizkind} with quiz-id ${useid}`);
    }
    const progression = get(path('progression'));
    const isrunning = get(path('progressionState', 'isrunning'));
    const progressionWrapperContent = [
        v('div', { key: `${useid}-quizwrapper`, xqa: `${kind} ${useid}` }, [
            w(QuizTitle, { key: `title-${key}`, title: quizTitle, progression, store })
        ]),
        hasQuizPattern || isFlashcards
            ? v('div', { key: `${useid}-progwrapper` }, [
                v('div', { key: `${useid}-p1` }, [
                    w(ProgressionWrapper, { key: `${useid}`, store, quizid: useid, quizkind })
                ])
            ])
            : w(WhichPath, { topicid, quizpatterns: quizPatterns, store }),
        w('TocSlidePane', {})
    ];
    const useMicroView = has('use-micro-view');
    return v('div', {
        key: `${useid}-${key}`,
        classes: [root, progression, useMicroView && !isrunning ? microViewWrapper : null].filter((v) => Boolean(v))
    }, useMicroView ? [v('div', { classes: [microView] }, progressionWrapperContent)] : progressionWrapperContent);
});
